import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ConnectWalletButton from './ConnectWalletButton';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { NavButtonStyle } from './styles/ButtonStyles';
import { ethers } from 'ethers';
import { useAccount } from 'wagmi';
import { useTokenContract } from '../contract/tokenHook';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  z-index: 999;
  background-color: transparent;
  
  @media (max-width: 768px) {
    background-color: rgba(255, 255, 255, 0.9);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #10d174;
  -webkit-text-stroke: 1px black;
  cursor: pointer;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  
  @media (max-width: 768px) {
    position: absolute;
    top: 60px;
    left: 0;
    right: 0;
    flex-direction: column;
    width: 100%;
    background-color: rgba(255, 255, 255, 0.95);
    padding: 20px;
    box-sizing: border-box;
    transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
    transition: transform 0.3s ease;
    z-index: 998;
  }
`;

const StyledLink = styled.a`
  text-decoration: none;
  
  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
  }
`;

const NavButton = styled(NavButtonStyle)`
  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
  }
`;

const HamburgerMenu = styled.div`
  display: none;
  flex-direction: column;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: flex;
  }
  
  span {
    width: 25px;
    height: 3px;
    background-color: #10d174;
    margin: 2px 0;
    border-radius: 3px;
    transition: 0.3s;
    
    &:nth-child(1) {
      transform: ${props => props.$isOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'};
    }
    
    &:nth-child(2) {
      opacity: ${props => props.$isOpen ? '0' : '1'};
    }
    
    &:nth-child(3) {
      transform: ${props => props.$isOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none'};
    }
  }
`;

const NootBalance = styled(NavButtonStyle)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #10d174;
  color: black;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const NootIcon = styled.span`
  font-weight: bold;
  margin-right: 6px;
`;

export default function Header() {
  const router = useRouter();
  // Start with an empty path to avoid hydration mismatch
  const [currentPath, setCurrentPath] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [nootBalance, setNootBalance] = useState(null);
  const { address, isConnected } = useAccount();
  const { checkBalance } = useTokenContract(null, null);
  
  // Set the current path only after the component has mounted on the client
  useEffect(() => {
    setCurrentPath(router.pathname);
  }, [router.pathname]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [router.pathname]);

  // Fetch NOOT balance when address changes
  useEffect(() => {
    const fetchBalance = async () => {
      if (isConnected && address && checkBalance) {
        try {
          const balance = await checkBalance(address);
          setNootBalance(balance);
        } catch (error) {
          console.error("Error fetching NOOT balance:", error);
        }
      } else {
        setNootBalance(null);
      }
    };

    fetchBalance();
    // Poll balance every 30 seconds
    const interval = setInterval(fetchBalance, 30000);
    
    return () => clearInterval(interval);
  }, [address, isConnected, checkBalance]);

  const handleLogoClick = () => {
    router.push('/');
  };
  
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  
  return (
    <HeaderContainer>
      <Logo onClick={handleLogoClick}>NOOT CARDS</Logo>
      
      <HamburgerMenu onClick={toggleMenu} $isOpen={menuOpen}>
        <span></span>
        <span></span>
        <span></span>
      </HamburgerMenu>
      
      <NavLinks $isOpen={menuOpen}>
        <Link href="/game" passHref legacyBehavior>
          <StyledLink>
            <NavButton $active={currentPath === '/game'}>PLAY</NavButton>
          </StyledLink>
        </Link>
        
        <Link href="/rules" passHref legacyBehavior>
          <StyledLink>
            <NavButton $active={currentPath === '/rules'}>RULES</NavButton>
          </StyledLink>
        </Link>
        
        {isConnected && nootBalance !== null && (
          <NootBalance $active={true}>
            <NootIcon>$NOOT:</NootIcon> {parseFloat(ethers.formatEther(nootBalance)).toFixed(2)}
          </NootBalance>
        )}
        
        <ConnectWalletButton position="header" />
      </NavLinks>
    </HeaderContainer>
  );
} 